import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Tag, 
  Button, 
  Input, 
  Select, 
  Space, 
  Badge, 
  Avatar, 
  Typography, 
  Tooltip,
  Row,
  Col,
  Statistic,
  message
} from 'antd';
import { 
  SearchOutlined, 
  FilterOutlined, 
  ClockCircleOutlined, 
  UserOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;

const TicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, new: 0, open: 0, pending: 0, closed: 0, unassigned: 0, in_progress: 0 });
  const [sourceFilter, setSourceFilter] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {
        page: 1,
        limit: 100,
        search: searchText,
        status: statusFilter,
        priority: priorityFilter,
        assigned: assignedFilter,
        source: sourceFilter
      };

      const response = await axios.get('https://limitverify.ru/api/tickets', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        setTickets(response.data.tickets || []);
        setLastUpdated(new Date());
      } else {
        message.error('Ошибка загрузки тикетов');
      }
    } catch (error) {
      console.error('Ошибка загрузки тикетов:', error);
      message.error('Ошибка загрузки тикетов');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('https://limitverify.ru/api/tickets/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const refreshAll = async () => {
    try {
      await axios.post('https://limitverify.ru/api/email/sync', null, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.warn('email sync:', e?.message);
    }
    await Promise.all([loadTickets(), loadStats()]);
  };

  useEffect(() => {
    refreshAll();
  }, [searchText, statusFilter, priorityFilter, assignedFilter, sourceFilter]);

  const getStatusConfig = (status) => {
    const configs = {
      new: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Новый' },
      open: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Открыт' },
      in_progress: { color: 'blue', icon: <ClockCircleOutlined />, text: 'В работе' },
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ждём клиента' },
      resolved: { color: 'cyan', icon: <CheckCircleOutlined />, text: 'Решён' },
      closed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Закрыт' },
      cancelled: { color: 'gray', icon: <CloseCircleOutlined />, text: 'Отменен' }
    };
    return configs[status] || configs.new;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      1: { color: 'red', text: 'Критический' },
      2: { color: 'orange', text: 'Высокий' },
      3: { color: 'blue', text: 'Средний' },
      4: { color: 'cyan', text: 'Низкий' },
      5: { color: 'green', text: 'Минимальный' }
    };
    return configs[priority] || configs[3];
  };

  const getSourceConfig = (source) => {
    const configs = {
      telegram: { color: 'blue', text: 'Telegram' },
      form: { color: 'green', text: 'Форма' },
      email: { color: 'purple', text: 'Email' },
      phone: { color: 'orange', text: 'Телефон' }
    };
    return configs[source] || configs.form;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTicketClick = (ticket) => {
    navigate(`/tickets/${ticket.id}`);
  };

  const handleClaim = async (e, ticketId) => {
    e.stopPropagation();
    try {
      await axios.post(`https://limitverify.ru/api/tickets/${ticketId}/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success('Тикет взят в работу');
      refreshAll();
    } catch (err) {
      message.error('Не удалось взять тикет');
    }
  };

  return (
    <div style={{ padding: '0 0 24px' }}>
      {/* Статистика */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={4}>
          <Card>
            <Statistic title="Всего" value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Новые" value={stats.new} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Открытые" value={stats.open || stats.in_progress || 0} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Ждём клиента" value={stats.pending || 0} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Неназначенные" value={stats.unassigned || 0} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="Закрытые" value={stats.closed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Фильтры */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Поиск по клиенту, email, теме или серийному номеру"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: '400px' }}
            />
          </Col>
          <Col>
            <Space>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 150 }}
                placeholder="Статус"
              >
                <Option value="all">Все статусы</Option>
                <Option value="active">Активные</Option>
                <Option value="new">Новые</Option>
                <Option value="open">Открытые</Option>
                <Option value="pending">Ждём клиента</Option>
                <Option value="resolved">Решённые</Option>
                <Option value="closed">Закрытые</Option>
                <Option value="cancelled">Отмененные</Option>
              </Select>

              <Select
                value={sourceFilter}
                onChange={setSourceFilter}
                style={{ width: 130 }}
                placeholder="Канал"
              >
                <Option value="all">Все каналы</Option>
                <Option value="telegram">Telegram</Option>
                <Option value="email">Email</Option>
                <Option value="form">Форма</Option>
                <Option value="phone">Телефон</Option>
              </Select>
              
              <Select
                value={priorityFilter}
                onChange={setPriorityFilter}
                style={{ width: 120 }}
                placeholder="Приоритет"
              >
                <Option value="all">Все приоритеты</Option>
                <Option value="1">Критический</Option>
                <Option value="2">Высокий</Option>
                <Option value="3">Средний</Option>
                <Option value="4">Низкий</Option>
                <Option value="5">Минимальный</Option>
              </Select>
              
              <Select
                value={assignedFilter}
                onChange={setAssignedFilter}
                style={{ width: 150 }}
                placeholder="Назначен"
              >
                <Option value="all">Все сотрудники</Option>
                <Option value="unassigned">Не назначен</Option>
                <Option value="me">Мне</Option>
              </Select>

              <Button
                type="primary"
                danger
                icon={<ReloadOutlined />}
                onClick={() => refreshAll()}
                loading={loading}
              >
                Обновить
              </Button>
              {lastUpdated && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {lastUpdated.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Список тикетов */}
      <Card>
        <List
          loading={loading}
          dataSource={tickets}
          renderItem={(ticket) => {
            const statusConfig = getStatusConfig(ticket.status);
            const priorityConfig = getPriorityConfig(ticket.priority);
            const sourceConfig = getSourceConfig(ticket.source);
            
            return (
              <List.Item
                onClick={() => handleTicketClick(ticket)}
                style={{ 
                  cursor: 'pointer',
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  transition: 'background-color 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                actions={[
                  !ticket.assigned_to_id ? (
                    <Button
                      type="primary"
                      size="small"
                      onClick={(e) => handleClaim(e, ticket.id)}
                    >
                      Взять
                    </Button>
                  ) : (
                    <Button
                      type="link"
                      size="small"
                      icon={<MessageOutlined />}
                    >
                      Открыть
                    </Button>
                  )
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Badge count={ticket.unread_count || 0} overflowCount={99}>
                      <Avatar icon={<UserOutlined />} />
                    </Badge>
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Text strong style={{ fontSize: '16px' }}>{ticket.subject}</Text>
                      <Tag color={statusConfig.color} icon={statusConfig.icon}>
                        {statusConfig.text}
                      </Tag>
                      <Tag color={priorityConfig.color}>
                        {priorityConfig.text}
                      </Tag>
                      <Tag color={sourceConfig.color}>
                        {sourceConfig.text}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong>{ticket.client_name}</Text>
                        <Text type="secondary" style={{ marginLeft: '8px' }}>
                          ({ticket.client_email})
                        </Text>
                      </div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text>{ticket.description}</Text>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#666' }}>
                        <span>Изделие: {ticket.product_name} ({ticket.product_serial})</span>
                        <span>Создан: {formatDate(ticket.created_at)}</span>
                        {ticket.assigned_to && (
                          <span>Назначен: {ticket.assigned_to}</span>
                        )}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
          locale={{ emptyText: 'Нет тикетов, соответствующих фильтрам' }}
        />
      </Card>
    </div>
  );
};

export default TicketList;
